import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebase";
import { useAppHeader } from "./ui/AppShell";
import {
  AsyncState,
  EmptyState,
  ErrorState,
  LoadingState,
  useAsync,
} from "./ui/AsyncState";
import { useToast } from "./ui/Toast";
import { copyToClipboard } from "./utils/clipboard";
import { canOpenExternalLinks } from "./utils/platform";
import { reportError } from "./utils/reportError";
import type { PdfLoadError } from "./PdfDocumentView";

/**
 * Propozice závodu (/pdfviewer/:raceId) – PDF z races.rulesPdfUrl.
 *
 * Fullscreen obrazovka AppShellu (App.tsx): prohlížeč vyplní plochu mezi
 * hlavičkou a spodní navigací a posouvá se sám (jediný posuvník). V hlavičce
 * „Otevřít PDF" – nová karta prohlížeče; nový WebView obal (LKSApp) předá
 * target=_blank systému (prohlížeč / PDF aplikace). Starý obal odkaz ven
 * otevřít neumí – místo něj se nabízí zkopírování odkazu
 * (canOpenExternalLinks). Když pdf.js dokument nevykreslí (worker, CORS,
 * poškozený soubor), ukáže se chyba s odkazem „Otevřít PDF přímo".
 */

/**
 * pdf.js (PdfDocumentView) je samostatný chunk – načte se až tady, po kontrole
 * podpory prohlížeče (pdfWorkerSupported). Hlavní bundle tak pdf.js
 * neobsahuje (menší, rychlejší start celé aplikace) a ve starém WebView se
 * jeho kód nikdy nespustí. Webpack ho rozdělí na pdfjs.<hash>.chunk.js
 * a chunk s knihovnou (číslo). Pojistka v index.html líně načítané chunky
 * (*.chunk.js) nereloaduje: nezdařené stažení (offline) řeší tato obrazovka.
 */
type PdfModule = typeof import("./PdfDocumentView");
const loadPdfModule = (): Promise<PdfModule> =>
  import(/* webpackChunkName: "pdfjs" */ "./PdfDocumentView");

/**
 * Plocha prohlížeče mezi hlavičkou a spodní navigací. Leží absolutně v mainu
 * (position: relative) od spodní hrany hlavičky po spodek okna, takže ji
 * neposune nic, co AppShell vykreslí v mainu před obsahem routy (offline
 * pruh): main se neposouvá a nic nezajede pod navigaci. Offline pruh tu
 * zůstane schovaný pod plochou (neprůhledné pozadí – jinak by prosvítal
 * přes chybový stav) – výpadek sítě ukáže chybový stav prohlížeče.
 * (Čistší řešení – flex sloupec v .lks-main--fullscreen – patří do ui.css.)
 * Fullscreen main nemá spodní ani boční padding: výšku navigace odečítá
 * padding z --lks-nav-real (měří AppShell), boky vrací safe-area (výřez
 * iPhonu na šířku).
 */
const FILL_STYLE: React.CSSProperties = {
  position: "absolute",
  top: "var(--lks-header-real, calc(var(--safe-top, 0px) + var(--lks-header-h, 3.5rem)))",
  right: 0,
  bottom: 0,
  left: 0,
  overflow: "hidden",
  backgroundColor: "var(--lks-bg)",
  paddingBottom: "var(--lks-nav-real, var(--safe-bottom, 0px))",
  paddingLeft: "env(safe-area-inset-left, 0px)",
  paddingRight: "env(safe-area-inset-right, 0px)",
};

/**
 * Zvládne prohlížeč pdf.js 4 (legacy build)? Worker se kopíruje do public/
 * nepřeložený (browserslist „chrome >= 80" překládá jen bundle aplikace)
 * a legacy build pdf.js 4.10 cílí na Chrome 103+ / Safari 16.4+: syntaxe
 * ES2022 – statické bloky tříd (Chrome 94, Safari 16.4), privátní metody
 * a `#x in obj`, logické přiřazení; bez polyfillu zůstávají
 * structuredClone (Chrome 98, Safari 15.4), Array.prototype.at (Chrome 92)
 * a replaceAll (Chrome 85). Worker běží jako ES modul
 * (Worker type: "module" – Chrome 80, Safari 15). Starý WebView obal by
 * skončil chybou „fake worker" nebo nevykreslenými písmy. Zjišťuje se
 * podporou, ne podle User-Agentu – stejně dopadne i nový obal na zařízení
 * se zastaralým WebView.
 */
export function pdfWorkerSupported(): boolean {
  if (typeof (Array.prototype as { at?: unknown }).at !== "function") return false;
  if (typeof (String.prototype as { replaceAll?: unknown }).replaceAll !== "function") return false;
  if (typeof (globalThis as { structuredClone?: unknown }).structuredClone !== "function") {
    return false;
  }
  try {
    // eslint-disable-next-line no-new-func
    new Function(
      "let a = 0; a ||= 1; a ??= 2;" +
        "class C { static #s = 1; static { C.#s = 2; } #m() { return a; }" +
        " t(o) { return #m in o && this.#m(); } }" +
        "return new C().t(new C());"
    );
    return true;
  } catch {
    return false;
  }
}

/** Starý obal: odkaz na PDF se zkopíruje (otevřít ven ho neumí). */
const OLD_APP_NOTE =
  "Tato verze aplikace LKS neumí otevírat odkazy mimo aplikaci – zkopírujte si odkaz a vložte ho do prohlížeče v telefonu.";

/**
 * Tlačítko „PDF mimo aplikaci": odkaz do nové karty, ve starém obalu
 * zkopírování odkazu (toast s výsledkem).
 */
const PdfOutsideButton: React.FC<{ pdfUrl: string; label: string; className: string }> = ({
  pdfUrl,
  label,
  className,
}) => {
  const { showToast } = useToast();
  const [canOpen] = useState(() => canOpenExternalLinks());
  if (canOpen) {
    return (
      <a className={className} href={pdfUrl} target="_blank" rel="noopener noreferrer">
        <i className="bi bi-box-arrow-up-right me-2" aria-hidden="true" />
        {label}
      </a>
    );
  }
  const copy = async () => {
    const ok = await copyToClipboard(pdfUrl);
    showToast(
      ok
        ? "Odkaz na propozice zkopírován – vložte ho do prohlížeče v telefonu."
        : `Odkaz se nepodařilo zkopírovat: ${pdfUrl}`,
      ok ? "success" : "warning"
    );
  };
  return (
    <button type="button" className={className} onClick={copy}>
      <i className="bi bi-clipboard me-2" aria-hidden="true" />
      Kopírovat odkaz na PDF
    </button>
  );
};

/**
 * Stavové obrazovky (načítám, nenalezeno) jako běžná stránka – odsazení
 * nad spodní navigací a od výřezů displeje, které fullscreen main nedává.
 */
const PAGE_STYLE: React.CSSProperties = {
  paddingLeft: "calc(env(safe-area-inset-left, 0px) + var(--lks-gutter, 1rem))",
  paddingRight: "calc(env(safe-area-inset-right, 0px) + var(--lks-gutter, 1rem))",
  paddingBottom:
    "calc(var(--lks-nav-real, var(--safe-bottom, 0px)) + var(--lks-sp-4, 1rem))",
};

interface PdfData {
  /** Závod v databázi je. */
  raceFound: boolean;
  raceName: string;
  /** Odkaz na PDF (races.rulesPdfUrl), jen http(s); jinak null. */
  pdfUrl: string | null;
}

const RACE_MISSING: PdfData = { raceFound: false, raceName: "", pdfUrl: null };

const loadPdfData = async (raceId: string): Promise<PdfData> => {
  const snap = await getDoc(doc(db, "races", raceId));
  if (!snap.exists()) return RACE_MISSING;
  const data = snap.data();
  const raw = typeof data.rulesPdfUrl === "string" ? data.rulesPdfUrl.trim() : "";
  return {
    raceFound: true,
    raceName: typeof data.name === "string" ? data.name : "",
    // Jen http(s): hodnota jde do href i do pdf.js (žádné javascript: apod.).
    pdfUrl: /^https?:\/\//i.test(raw) ? raw : null,
  };
};

/** Spinner pdf.js se zpožděním jako ostatní obrazovky; procenta jen průběžně. */
const renderLoader = (percentages: number): React.ReactElement => (
  <LoadingState
    text={
      percentages > 0 && percentages < 100
        ? `Načítám propozice… ${Math.round(percentages)} %`
        : "Načítám propozice…"
    }
  />
);

/**
 * Chyba workeru pdf.js (ani záložní „fake" worker se nenačetl – hlášku
 * skládá pdf.js: „Setting up fake worker failed: …"). Nezdařený worker si
 * pdf.js pamatuje do konce života stránky: nastaví
 * PDFWorker.#isWorkerDisabled a zamítnutý slib uloží natrvalo do
 * PDFWorker._setupFakeWorkerGlobal (pdfjs-dist 4.10, legacy/build/pdf.mjs). Každý
 * další pokus – i nový mount prohlížeče – proto skončí stejnou chybou bez
 * nového požadavku; pomůže jen znovunačtení stránky.
 */
const isWorkerError = (error: PdfLoadError): boolean =>
  /fake worker/i.test(error.message || "");

/**
 * Worker se nestáhl (výpadek sítě, offline). pdf.js 4 po selhání modulového
 * workeru zkusí worker importovat dynamickým import() a hlásí chybu
 * prohlížeče: Chrome „Failed to fetch dynamically imported module", Safari
 * „Importing a module script failed.", Firefox „error loading dynamically
 * imported module" (pdf.js 3: „Cannot load script at …"). Jen tehdy má smysl
 * znovunačtení stránky; když se stáhl, ale nespustil (nepodporovaný
 * prohlížeč – SyntaxError, odpověď, která není JavaScript), reload nepomůže.
 */
const WORKER_NETWORK_ERROR =
  /cannot load script|failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module/i;

export const isWorkerNetworkError = (error: PdfLoadError): boolean =>
  isWorkerError(error) && WORKER_NETWORK_ERROR.test(error.message || "");

type ReloadResult = "reloading" | "offline" | "unreachable" | "cancelled";

/** Jak dlouho čekat na ověření připojení před reloadem (slabý signál). */
const REACHABILITY_TIMEOUT_MS = 6000;

/**
 * Znovunačtení stránky (reset workeru pdf.js), ale jen když je server
 * dosažitelný. Bez připojení by reload skončil chybovou stránkou prohlížeče
 * (aplikace nemá service worker) a ve WebView obalu by z ní nebyla cesta
 * zpět – proto nejdřív navigator.onLine a krátký HEAD na worker (s časovým
 * limitem). Reload jen pokud uživatel mezitím neodešel z propozic
 * (`stillHere`) – jinak by zahodil třeba rozepsanou přihlášku.
 */
const reloadIfReachable = async (
  workerUrl: string,
  signal: AbortSignal,
  stillHere: () => boolean
): Promise<ReloadResult> => {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return "offline";
  }
  try {
    const response = await fetch(workerUrl, {
      method: "HEAD",
      cache: "no-store",
      signal,
    });
    // Chybějící worker Netlify odbaví jako SPA (200 + index.html) – reload
    // by nepomohl, za dosažitelný se počítá jen skutečný JavaScript.
    const type = response.headers.get("content-type") || "";
    if (!response.ok || !/javascript/i.test(type)) return "unreachable";
  } catch {
    return stillHere() ? "unreachable" : "cancelled";
  }
  if (!stillHere()) return "cancelled";
  window.location.reload();
  return "reloading";
};

interface PdfRenderErrorProps {
  error: PdfLoadError;
  pdfUrl: string;
  onRetry: () => void;
  /** Worker pdf.js (s ?v=) – na něj jde ověřovací HEAD před reloadem. */
  workerUrl?: string;
}

/**
 * pdf.js dokument nevykreslil – nestáhl se chunk s pdf.js nebo worker
 * (offline, výpadek sítě), úložiště odmítlo CORS, soubor je poškozený…
 * ErrorState se „Zkusit znovu"
 * a odkaz na PDF mimo aplikaci. Po výpadku sítě při načítání workeru
 * „Zkusit znovu" znovu načte stránku (viz isWorkerError), po jiné chybě
 * workeru se nenabízí (reload by nepomohl), jinak se prohlížeč jen namountuje
 * znovu. Prohlížeč, který worker nezvládne (starý WebView obal), sem
 * nedojde – PdfViewer mu rovnou nabídne PDF mimo aplikaci
 * (pdfWorkerSupported).
 */
const PdfRenderError: React.FC<PdfRenderErrorProps> = ({
  error,
  pdfUrl,
  onRetry,
  workerUrl = `${process.env.PUBLIC_URL}/pdf.worker.min.js`,
}) => {
  const { showToast } = useToast();
  const workerFailed = isWorkerError(error);
  // PDF chráněné heslem – opakování nepomůže, heslo se zadá mimo aplikaci.
  const passwordProtected = error.name === "PasswordException";
  const canRetry = !passwordProtected && (!workerFailed || isWorkerNetworkError(error));
  // Ověření připojení před reloadem běží jen jednou (dvojí ťuknutí).
  const [checking, setChecking] = useState(false);
  const checkingRef = useRef(false);
  const mountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  useEffect(() => {
    reportError("PdfViewer.render", error, { name: error.name });
  }, [error]);

  const handleRetry = () => {
    if (!workerFailed) {
      onRetry();
      return;
    }
    if (checkingRef.current) return;
    checkingRef.current = true;
    setChecking(true);
    const controller = new AbortController();
    abortRef.current = controller;
    const timer = window.setTimeout(() => controller.abort(), REACHABILITY_TIMEOUT_MS);
    const stillHere = () =>
      mountedRef.current && window.location.pathname.indexOf("/pdfviewer/") === 0;
    reloadIfReachable(workerUrl, controller.signal, stillHere).then((result) => {
      window.clearTimeout(timer);
      if (result === "reloading" || result === "cancelled") return;
      checkingRef.current = false;
      setChecking(false);
      showToast(
        result === "offline"
          ? "Jste offline – propozice půjde načíst po připojení."
          : "Server teď není dostupný – zkuste to prosím za chvíli.",
        "warning"
      );
    });
  };

  return (
    <div style={{ height: "100%", overflowY: "auto" }}>
      <div className="lks-page lks-page--user">
        {canRetry ? (
          <ErrorState error={error} onRetry={handleRetry} />
        ) : (
          // Worker se stáhl, ale nespustil, nebo PDF chce heslo – opakování nepomůže, hláška
          // ErrorState („Zkuste to znovu") by vybízela k akci, která tu není
          <EmptyState
            icon={passwordProtected ? "bi-file-earmark-lock" : "bi-file-earmark-x"}
            title={
              passwordProtected
                ? "Propozice jsou chráněné heslem"
                : "Propozice se v aplikaci nedají zobrazit"
            }
            text={
              passwordProtected
                ? "Otevřete je mimo aplikaci – tam půjde heslo zadat."
                : "Vestavěné zobrazení PDF teď nefunguje – otevřete je mimo aplikaci."
            }
          />
        )}
        {checking && <LoadingState inline text="Ověřuji připojení…" />}
        <div className="text-center pb-4">
          <p className="text-body-secondary mb-3">
            {canOpenExternalLinks()
              ? "Propozice můžete otevřít i mimo aplikaci – v prohlížeči nebo v aplikaci pro PDF."
              : OLD_APP_NOTE}
          </p>
          <PdfOutsideButton
            pdfUrl={pdfUrl}
            label="Otevřít PDF přímo"
            className="btn btn-outline-primary"
          />
        </div>
      </div>
    </div>
  );
};

const PdfViewer: React.FC = () => {
  const { raceId } = useParams<{ raceId: string }>();
  const navigate = useNavigate();
  const { setHeader } = useAppHeader();
  // „Zkusit znovu" po chybě vykreslení = nový mount prohlížeče (nové
  // načtení); po chybě workeru pdf.js reload stránky (viz PdfRenderError).
  const [viewerKey, setViewerKey] = useState(0);

  const page = useAsync<PdfData>(
    () => (raceId ? loadPdfData(raceId) : Promise.resolve(RACE_MISSING)),
    [raceId]
  );
  const data = page.status === "ready" ? page.data : null;
  const raceName = data ? data.raceName : "";
  const pdfUrl = data ? data.pdfUrl : null;

  const { showToast } = useToast();
  const [canOpen] = useState(() => canOpenExternalLinks());

  // „Otevřít PDF" v hlavičce, jakmile je odkaz známý (i při chybě vykreslení);
  // starý obal odkaz ven neotevře – zkopíruje ho.
  const actions = useMemo(() => {
    if (!pdfUrl) return undefined;
    if (canOpen) {
      return (
        <a
          className="lks-header__btn text-decoration-none"
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Otevřít PDF"
        >
          <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
          {/* Na úzkém displeji jen ikona jako u Zpět (ui.css, 23,5em). */}
          <span className="lks-header__btn-text">Otevřít PDF</span>
        </a>
      );
    }
    const copy = async () => {
      const ok = await copyToClipboard(pdfUrl);
      showToast(
        ok
          ? "Odkaz na propozice zkopírován – vložte ho do prohlížeče v telefonu."
          : `Odkaz se nepodařilo zkopírovat: ${pdfUrl}`,
        ok ? "success" : "warning"
      );
    };
    return (
      <button
        type="button"
        className="lks-header__btn"
        aria-label="Kopírovat odkaz na PDF"
        onClick={copy}
      >
        <i className="bi bi-clipboard" aria-hidden="true" />
        <span className="lks-header__btn-text">Kopírovat odkaz</span>
      </button>
    );
  }, [pdfUrl, canOpen, showToast]);

  useLayoutEffect(() => {
    setHeader({
      title: "Propozice",
      subtitle: raceName || undefined,
      back: "history",
      actions,
    });
  }, [setHeader, raceName, actions]);

  // Stejně jako Zpět v hlavičce: historií, při přímém otevření na přehled.
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/user");
  };

  // Jednou za život obrazovky – výsledek se nemění
  const [workerSupported] = useState(pdfWorkerSupported);

  // Chunk s pdf.js: stáhne se, až je co zobrazit a prohlížeč pdf.js zvládne.
  // Nezdařené stažení (offline) = chybový stav; „Zkusit znovu" ho stáhne
  // znovu (webpack po chybě chunk znovu požádá), bez reloadu stránky.
  const wantsPdf = !!(data && pdfUrl && workerSupported);
  const [pdfModule, setPdfModule] = useState<PdfModule | null>(null);
  const [moduleError, setModuleError] = useState<PdfLoadError | null>(null);
  const [moduleAttempt, setModuleAttempt] = useState(0);
  useEffect(() => {
    if (!wantsPdf || pdfModule) return;
    let active = true;
    loadPdfModule().then(
      (mod) => {
        if (active) setPdfModule(mod);
      },
      (err: unknown) => {
        if (!active) return;
        const e = err as { message?: unknown; name?: unknown } | null;
        setModuleError({
          message: e && typeof e.message === "string" ? e.message : "Loading chunk failed",
          name: e && typeof e.name === "string" ? e.name : undefined,
        });
      }
    );
    return () => {
      active = false;
    };
  }, [wantsPdf, pdfModule, moduleAttempt]);

  if (data && pdfUrl && !workerSupported) {
    return (
      <div className="lks-page lks-page--user" style={PAGE_STYLE}>
        <EmptyState
          icon="bi-file-earmark-pdf"
          title="Propozice otevřete mimo aplikaci"
          text={
            canOpen
              ? "Tento prohlížeč neumí vestavěné zobrazení PDF. Propozice se otevřou v prohlížeči nebo v aplikaci pro PDF."
              : `Tento telefon neumí vestavěné zobrazení PDF. ${OLD_APP_NOTE}`
          }
        />
        <div className="text-center pb-4">
          <PdfOutsideButton pdfUrl={pdfUrl} label="Otevřít PDF" className="btn btn-primary" />
        </div>
      </div>
    );
  }

  if (data && pdfUrl) {
    if (moduleError) {
      return (
        <div style={FILL_STYLE}>
          <PdfRenderError
            error={moduleError}
            pdfUrl={pdfUrl}
            onRetry={() => {
              setModuleError(null);
              setModuleAttempt((n) => n + 1);
            }}
          />
        </div>
      );
    }
    if (!pdfModule) {
      return <div style={FILL_STYLE}>{renderLoader(0)}</div>;
    }
    const { default: PdfDocumentView, PDF_WORKER_URL } = pdfModule;
    return (
      <div style={FILL_STYLE}>
        <PdfDocumentView
          key={viewerKey}
          fileUrl={pdfUrl}
          renderLoader={renderLoader}
          renderError={(error) => (
            <PdfRenderError
              error={error}
              pdfUrl={pdfUrl}
              workerUrl={PDF_WORKER_URL}
              onRetry={() => setViewerKey((key) => key + 1)}
            />
          )}
        />
      </div>
    );
  }

  const back = { label: "Zpět", onClick: goBack };

  return (
    <div className="lks-page lks-page--user" style={PAGE_STYLE}>
      <AsyncState
        status={page.status}
        error={page.error}
        onRetry={page.reload}
        loadingText="Načítám propozice…"
      >
        {!data || !data.raceFound ? (
          <EmptyState
            icon="bi-calendar-x"
            title="Závod nenalezen"
            text="Závod byl zrušen nebo odkaz už neplatí."
            action={back}
          />
        ) : (
          <EmptyState
            icon="bi-file-earmark-x"
            title="Propozice nejsou k dispozici"
            text={
              data.raceName
                ? `K závodu „${data.raceName}“ zatím nejsou nahrané propozice.`
                : "K závodu zatím nejsou nahrané propozice."
            }
            action={back}
          />
        )}
      </AsyncState>
    </div>
  );
};

export default PdfViewer;
